import * as api from '../api'
import { setUser } from '@doordash/lib-monitoring'
import { get } from 'lodash'
import { createOnboardingRequestDirect, SUBMIT_DASHER_APPLICATION } from '.'
import {
  NON_MARKET_ERROR,
  returnToApplicationEmailErrorMessages,
  returnToApplicationPhoneErrorMessages,
  returnToApplicationZipcodeErrorMessages,
} from '../constants'
import * as events from '../events'
import { deleteUserSessionToken } from '../utilities/SimpleStore'
import { getUtmSource } from '../utilities/utmSource'
import { getUrlVars } from '../utilities/window'
import { ApplicantData, ApplicationData } from '../api'
import { isFeatureFlagEnabled } from '@doordash/lib-feature-flags'
import * as Exp from '../experimentConstants'
import { stripDasherPlatformErrorPrefix } from '../utilities/application/application'

export const submitDasherApplication = (applicationData: ApplicantData) => {
  return async (dispatch, getState) => {
    dispatch({ type: SUBMIT_DASHER_APPLICATION.IN_PROGRESS })

    const { referred_by, source } = getUrlVars()
    const utmSource = getUtmSource()
    const referrerId = getState().referrer.id
    const referralCampaign = getState().referralCampaignId
    const signupCountryShortname = getState().signupCountryShortname

    const requestPayload: ApplicationData = {
      ...applicationData,
      referralDomain: document.referrer || undefined,
      referredBy: referrerId || referred_by,
      source: source || utmSource,
      referralCampaign: referralCampaign || undefined,
      signupCountryShortname: signupCountryShortname || undefined,
    }

    try {
      const shouldUseDxPlatform =
        isFeatureFlagEnabled(Exp.DX_ONBOARDING_V2) ||
        isFeatureFlagEnabled(Exp.GERMANY_DX_ONBOARDING_V2)
      const { email, phoneNumber, zipCode } = applicationData
      const dxPlatformPayload = {
        geoLocator: {
          zip: zipCode,
          country: getState().signupCountryShortname,
        },
        personLocator: { email, phoneNumber },
      }

      // HACK: prevent dasher applications from being tied
      // to the currently logged in user when going to the
      // dasher application flow **when you've just signed up**
      //
      // See https://doordash.atlassian.net/browse/DX-2212
      // and https://github.com/doordash/doorstep-django/pull/5652
      deleteUserSessionToken()

      const data = shouldUseDxPlatform
        ? await createOnboardingRequestDirect(dispatch, dxPlatformPayload)
        : await api.submitDasherApplication(requestPayload)

      dispatch({ type: SUBMIT_DASHER_APPLICATION.SUCCESS, payload: data })

      await events.identifyDasher(data)
      setUser({ id: data.uniqueLink })

      dispatch(
        events.trackDasherApplySubmissionSuccess({ ...data, ...requestPayload })
      )
      return data
    } catch (e) {
      let error = e

      // Reformat Dasher Plaform error messages into a usable format similar to DSJ. Ideally this should be moved to BFF logic.
      const responseData = get(e, 'response.data')
      if (!error.hasOwnProperty('fieldErrors')) {
        const errorMsg = stripDasherPlatformErrorPrefix(responseData)
        error = {
          fieldErrors: {
            email: returnToApplicationEmailErrorMessages.has(errorMsg)
              ? errorMsg
              : undefined,
            phoneNumber: returnToApplicationPhoneErrorMessages.has(errorMsg)
              ? errorMsg
              : undefined,
            zipCode: returnToApplicationZipcodeErrorMessages.has(errorMsg)
              ? errorMsg
              : undefined,
          },
        }
      }

      if (get(error, 'fieldErrors.zipCode') === NON_MARKET_ERROR) {
        const { submarket } = getState().dasherApplyPageContent
        dispatch(
          events.trackDasherApplyNonMarket({
            submarket: submarket || '',
            zipCode: applicationData.zipCode,
          })
        )
      }

      dispatch(events.trackDasherApplySubmissionFailure)
      if (error.error) {
        dispatch(
          events.trackGESubmissionFailureInvalidZip(applicationData.zipCode)
        )
        if (error.campaign) {
          // todo: rework this to use FSA Actions
          // (need to reshape reducers to match)
          dispatch({
            applicationData,
            type: 'GUARANTEED_EARNINGS_WRONG_ZIP.CAMPAIGN_AVAILABLE',
            campaign: error.campaign,
          })
        } else {
          dispatch({
            applicationData,
            type: 'GUARANTEED_EARNINGS_WRONG_ZIP.CAMPAIGN_NOT_AVAILABLE',
          })
        }
      } else {
        dispatch({ type: SUBMIT_DASHER_APPLICATION.FAIL, payload: error })
      }
      throw error
    }
  }
}
